import { useState, type FormEvent } from 'react';
import { apiRequest, ApiError } from '../lib/api';
import { Alert, Button, Field, TextInput } from '../components/ui';

/**
 * "Report this listing" (§38).
 *
 * Deliberately small and deliberately at the BOTTOM of the page it appears on.
 * The people who need it have already read the listing and rung the number;
 * putting it beside the phone number would invite the misclick and give the
 * page a second, competing call to action.
 *
 * Closed until asked for, because most readers are not reporting anything and
 * a permanently open form implies the listing is suspect.
 *
 * What it does NOT do is promise an outcome. The report reaches a person who
 * will look; saying "this will be removed" would be a promise the product
 * cannot keep, and §38 is explicit that an administrator investigates rather
 * than the flag acting on its own.
 */
export function ReportListing(
  { targetType, targetId, what }: {
    targetType: 'provider' | 'item' | 'listing' | 'sale_listing';
    targetId: string;
    /** What is being reported, for the prompt: 'business', 'item', 'listing'. */
    what: string;
  },
) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await apiRequest('/api/v1/public/reports', {
        method: 'POST',
        body: {
          targetType, targetId, reason: reason.trim(),
          ...(phone.trim() ? { reporterPhone: phone.trim() } : {}),
        },
      });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.status === 429
            ? 'That is several reports in a short time. Please try again later.'
            : err.message)
          : 'That could not be sent. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="report-box">
        <Alert tone="success">
          Thank you. Somebody at NyumbaSuite will look at this {what}.
        </Alert>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="report-box">
        <button type="button" className="report-link" onClick={() => setOpen(true)}>
          Report this {what}
        </button>
      </div>
    );
  }

  return (
    <div className="report-box">
      <form onSubmit={submit}>
        <p className="muted" style={{ marginTop: 0 }}>
          Tell us what is wrong — a number that is not theirs, a business that has
          closed, something that looks like a fraud. A person reads every report.
        </p>
        {error && <div style={{ marginBottom: 12 }}><Alert tone="danger">{error}</Alert></div>}

        <Field label="What is wrong with it?" htmlFor="rep-reason">
          <textarea id="rep-reason" className="textarea" rows={3} required
            minLength={10} maxLength={2000} value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="The number belongs to somebody else — they told me they have never heard of this business." />
        </Field>
        <Field label="Your phone (optional, if we may ask you about it)" htmlFor="rep-phone">
          <TextInput id="rep-phone" value={phone} onChange={e => setPhone(e.target.value)}
            placeholder="+254 7xx xxx xxx" />
        </Field>

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <Button type="submit" loading={busy} disabled={reason.trim().length < 10}>
            Send report
          </Button>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
